import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, AlertTriangle, ArrowRight, Clock, TrendingUp, ClipboardCheck, FileText, UserPlus, Plus, BookOpen, Target } from 'lucide-react';
import { getModuleIcon, MODULES, getFamiliesForModule, getAllowedTiersForGroup, humanizeFamilyId } from '../../../content';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { PageLayout } from '../../shared/PageLayout';
import { EmptyState } from '../../../components/shared/EmptyState';
import { StudentData } from './StudentDetailPanel';
import { DjayNarrator } from '../../../mascot';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { getAvatarGradient } from '../utils/avatarUtils';
import { V11_CONFIG } from '../../../mastery';
import { getFamilyMasteryKey } from '../../../mastery';
import { getStudentInsights, DifferentiationInsight } from '../utils/differentiationInsights';
import { GroupId } from '../../../config/systemConfig';
import { formatRelativeTime } from '../../../utils/formatters';
import { containerVariants, itemVariants, listContainerVariants, listItemVariants } from '../shared/animationVariants';
import { DataFreshness } from '../../shared/DataFreshness';
import { PriorityAlertBar, type PriorityAlert } from '../components/PriorityAlertBar';
/** Days of inactivity before a student is flagged as "long inactive" (MN-02) */
const LONG_INACTIVE_DAYS = 14;
const LONG_INACTIVE_MS = LONG_INACTIVE_DAYS * 24 * 60 * 60 * 1000;
// Simple animated number component (SC-3: added cancelAnimationFrame cleanup)
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let startTime: number;
    let rafId: number;
    const duration = 1000; // 1 second
    const startValue = 0;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutQuart
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(startValue + easeProgress * (value - startValue)));
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [value]);
  return <>{displayValue}</>;
}
interface TodayPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  onNavigate: (route: string) => void;
  classroomName?: string;
}
export function TodayPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent,
  onNavigate,
  classroomName
}: TodayPageProps) {
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  const stats = useMemo(() => {
    if (students.length === 0) return null;
    const strugglingStudents: Array<{
      student: StudentData;
      insight: DifferentiationInsight;
    }> = [];
    let studentsPracticedThisWeek = 0;
    let totalMasterySum = 0;
    let totalMasteryCount = 0;
    const moduleMasterySums: Record<string, {
      sum: number;
      count: number;
    }> = {};
    MODULES.forEach(m => {
      moduleMasterySums[m.id] = {
        sum: 0,
        count: 0
      };
    });
    const allowedTiers = getAllowedTiersForGroup(selectedGroup);
    const familyStatsMap: Record<string, {
      familyId: string;
      moduleId: string;
      label: string;
      totalMastery: number;
      practicedCount: number;
      strugglingCount: number;
    }> = {};
    const inactiveStudents = students.filter(student => {
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      return weeklyActivity.filter(d => d.practiced).length === 0;
    }).map(student => {
      let lastActive = 0;
      if (student.data.questionStates) {
        for (const state of Object.values(student.data.questionStates)) {
          if (state.lastAttemptDate > lastActive) {
            lastActive = state.lastAttemptDate;
          }
        }
      }
      return {
        student,
        lastActive
      };
    }).sort((a, b) => b.lastActive - a.lastActive);
    // Group context is needed for insights, we can extract it from the first student's data if available
    // or fallback to 6. In a real app, selectedGroup would be passed as a prop.
    // Since TodayPage doesn't receive selectedGroup, we'll try to infer it or default to 6.
    // Actually, we can just pass 6 as a fallback, but let's try to get it from the first student's groupVersion if possible.
    // Wait, getStudentInsights needs a GroupId. Let's just use 6 as a fallback since we don't have it in props.
    // A better way: TodayPage is rendered inside TeacherShellV2 which has selectedGroup.
    // Let's add selectedGroup to TodayPageProps.
    students.forEach(student => {
      let hasPracticed = false;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          hasPracticed = true;
          totalMasterySum += mastery;
          totalMasteryCount++;
          moduleMasterySums[module.id].sum += mastery;
          moduleMasterySums[module.id].count++;
        }
      });
      if (hasPracticed) {
        // Use the selectedGroup from props
        const insights = getStudentInsights(student.data, selectedGroup, 1);
        if (insights.length > 0 && insights[0].mastery < V11_CONFIG.STRUGGLING_THRESHOLD) {
          strugglingStudents.push({
            student,
            insight: insights[0]
          });
        }
      }
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      if (weeklyActivity.some(d => d.practiced)) {
        studentsPracticedThisWeek++;
      }
      // Calculate family stats for class-wide weak spots
      MODULES.forEach(module => {
        const families = getFamiliesForModule(module.id);
        const relevantFamilies = families.filter(f => allowedTiers.has(f.tier));
        relevantFamilies.forEach(f => {
          const key = getFamilyMasteryKey(f.familyId, selectedGroup);
          const fm = student.data.familyMastery?.[key];
          if (fm && fm.attempts > 0) {
            if (!familyStatsMap[f.familyId]) {
              familyStatsMap[f.familyId] = {
                familyId: f.familyId,
                moduleId: module.id,
                label: humanizeFamilyId(f.familyId),
                totalMastery: 0,
                practicedCount: 0,
                strugglingCount: 0
              };
            }
            familyStatsMap[f.familyId].practicedCount++;
            familyStatsMap[f.familyId].totalMastery += fm.masteryScore;
            if (fm.masteryScore < V11_CONFIG.STRUGGLING_THRESHOLD) {
              familyStatsMap[f.familyId].strugglingCount++;
            }
          }
        });
      });
    });
    const classWideWeakSpots = Object.values(familyStatsMap).map(f => ({
      ...f,
      avgMastery: Math.round(f.totalMastery / f.practicedCount)
    })).filter(f => f.strugglingCount >= 2) // Only show if multiple students struggle
    .sort((a, b) => {
      if (b.strugglingCount !== a.strugglingCount) {
        return b.strugglingCount - a.strugglingCount;
      }
      return a.avgMastery - b.avgMastery;
    }).slice(0, 5);
    const classAverageMastery = totalMasteryCount > 0 ? Math.round(totalMasterySum / totalMasteryCount) : 0;
    let strongestModule = MODULES[0];
    let weakestClassModule = MODULES[0];
    let highestAvg = -1;
    let lowestAvg = 101;
    MODULES.forEach(m => {
      const mStats = moduleMasterySums[m.id];
      if (mStats.count > 0) {
        const avg = mStats.sum / mStats.count;
        if (avg > highestAvg) {
          highestAvg = avg;
          strongestModule = m;
        }
        if (avg < lowestAvg) {
          lowestAvg = avg;
          weakestClassModule = m;
        }
      }
    });
    if (highestAvg === -1) {
      strongestModule = MODULES[0];
      weakestClassModule = MODULES[0];
    }
    return {
      strugglingStudents: strugglingStudents.sort((a, b) => a.insight.mastery - b.insight.mastery).slice(0, 6),
      totalStruggling: strugglingStudents.length,
      inactiveStudents,
      studentsPracticedThisWeek,
      classAverageMastery,
      strongestModule,
      weakestClassModule,
      highestAvg,
      lowestAvg,
      classWideWeakSpots
    };
  }, [students, getModuleMastery, selectedGroup]);
  const alerts = useMemo(() => {
    if (!stats) return [];
    const newAlerts: PriorityAlert[] = [];
    stats.strugglingStudents.forEach(({
      student,
      insight
    }) => {
      newAlerts.push({
        id: `struggle-${student.id}`,
        tone: 'urgent',
        icon: AlertTriangle,
        message: `${student.name} loopt vast op ${insight.familyName}`,
        onClick: () => onSelectStudent(student.id)
      });
    });
    if (newAlerts.length < 5) {
      stats.inactiveStudents.forEach(({
        student,
        lastActive
      }) => {
        if (newAlerts.length >= 5) return;
        if (lastActive === 0 || Date.now() - lastActive > LONG_INACTIVE_MS) {
          newAlerts.push({
            id: `inactive-${student.id}`,
            tone: 'warning',
            icon: Clock,
            message: `${student.name} is al ${lastActive === 0 ? 'nog nooit' : formatRelativeTime(lastActive)} niet actief`,
            onClick: () => onSelectStudent(student.id)
          });
        }
      });
    }
    return newAlerts;
  }, [stats, onSelectStudent]);
  const hour = new Date().getHours();
  let greeting = 'Goedenavond';
  let greetingIcon = '🌙';
  if (hour >= 6 && hour < 12) {
    greeting = 'Goedemorgen';
    greetingIcon = '☀️';
  } else if (hour >= 12 && hour < 18) {
    greeting = 'Goedemiddag';
    greetingIcon = '🌤️';
  }
  const dateString = new Date().toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  if (students.length === 0) {
    return <PageLayout title="Vandaag" description="Overzicht van de klas" actions={<Button onClick={() => setIsAddStudentOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10196">
            <Plus className="w-4 h-4" data-id="element-10197" />
            Leerling Toevoegen
          </Button>} data-id="element-10195">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-10198">
          <EmptyState title="Nog geen leerlingen" message="Je klas is nog leeg. Voeg leerlingen toe om hun voortgang te volgen en inzicht te krijgen in hun prestaties." emotion="thinking" actionLabel="Naar Mijn Klas" onAction={() => onNavigate('class')} colorScheme="indigo" data-id="element-10199" />
        </motion.div>
      </PageLayout>;
  }
  if (!stats) return null;
  const StrongestIcon = getModuleIcon(stats.strongestModule.icon);
  const WeakestIcon = getModuleIcon(stats.weakestClassModule.icon);
  return <PageLayout title="Vandaag" description={classroomName ? `Actuele inzichten voor ${classroomName}` : 'Actuele inzichten en aandachtspunten'} data-id="element-10200">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10201">
        <div className="flex justify-end mb-[-12px]" data-id="element-10202">
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={() => setLastLoadedAt(new Date())} data-id="element-10203" />
        </div>

        {/* Welcome Block */}
        <motion.div variants={itemVariants} className="bg-indigo-600 rounded-2xl p-4 text-white shadow-md relative overflow-hidden" data-id="element-10204">
          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-10205">
            <div data-id="element-10206">
              <h1 className="text-2xl font-black mb-1" data-id="element-10207">
                {greetingIcon} {greeting}!
              </h1>
              <p className="text-indigo-100 font-medium text-sm" data-id="element-10208">
                {classroomName || 'Mijn Klas'} •{' '}
                <span className="capitalize" data-id="element-10209">{dateString}</span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm font-medium bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10" data-id="element-10210">
              <div className="flex items-center gap-1.5" data-id="element-10211">
                <Users className="w-4 h-4 text-indigo-200" data-id="element-10212" />
                <span data-id="element-10213">{students.length} leerlingen</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-10214"></div>
              <div className="flex items-center gap-1.5" data-id="element-10215">
                <TrendingUp className="w-4 h-4 text-emerald-300" data-id="element-10216" />
                <span data-id="element-10217">{stats.studentsPracticedThisWeek} actief</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-10218"></div>
              <div className="flex items-center gap-1.5" data-id="element-10219">
                <AlertTriangle className={`w-4 h-4 ${stats.totalStruggling > 0 ? 'text-rose-300' : 'text-indigo-200'}`} data-id="element-10220" />
                <span data-id="element-10221">{stats.totalStruggling} aandachtspunten</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Priority Alerts */}
        <motion.div variants={itemVariants} data-id="element-10222">
          <PriorityAlertBar alerts={alerts} maxVisible={3} data-id="element-10223" />
        </motion.div>

        {/* Quick Actions Row */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10224">
          <button onClick={() => onNavigate('homework')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10225">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-10226">
              <BookOpen className="w-5 h-5" data-id="element-10227" />
            </div>
            <div data-id="element-10228">
              <div className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors" data-id="element-10229">
                Huiswerk
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10230">
                Naar overzicht
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('assessments')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10231">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors" data-id="element-10232">
              <ClipboardCheck className="w-5 h-5" data-id="element-10233" />
            </div>
            <div data-id="element-10234">
              <div className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors" data-id="element-10235">
                Nieuwe toets
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10236">
                Toets inplannen
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('reports')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10237">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors" data-id="element-10238">
              <FileText className="w-5 h-5" data-id="element-10239" />
            </div>
            <div data-id="element-10240">
              <div className="font-bold text-slate-900 group-hover:text-emerald-700 transition-colors" data-id="element-10241">
                Rapporten
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10242">
                Resultaten bekijken
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('management')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10243">
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition-colors" data-id="element-10244">
              <UserPlus className="w-5 h-5" data-id="element-10245" />
            </div>
            <div data-id="element-10246">
              <div className="font-bold text-slate-900 group-hover:text-amber-700 transition-colors" data-id="element-10247">
                Leerling toevoegen
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10248">
                Klas beheren
              </div>
            </div>
          </button>
        </motion.div>

        {/* Class Overview Card */}
        <motion.div variants={itemVariants} data-id="element-10249">
          <Card className="bg-white border-slate-200 shadow-sm overflow-hidden p-5" data-id="element-10250">
            <div className="flex items-center gap-2 mb-4" data-id="element-10251">
              <TrendingUp className="w-5 h-5 text-indigo-600" data-id="element-10252" />
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider" data-id="element-10253">
                Klas Overzicht
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-10254">
              <div className="flex flex-col" data-id="element-10255">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10256">
                  Gemiddelde beheersing
                </div>
                <div className="flex items-end gap-2" data-id="element-10257">
                  <div className="text-3xl font-black text-slate-900" data-id="element-10258">
                    <AnimatedNumber value={stats.classAverageMastery} data-id="element-10259" />%
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0" data-id="element-10260">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10261">
                  Sterkste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-10262">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-10263">
                    <StrongestIcon className="w-4 h-4" data-id="element-10264" />
                  </div>
                  <div data-id="element-10265">
                    <div className="font-bold text-slate-900" data-id="element-10266">
                      {stats.strongestModule.title}
                    </div>
                    <div className="text-xs font-medium text-emerald-600" data-id="element-10267">
                      {stats.highestAvg > -1 ? Math.round(stats.highestAvg) : 0}
                      % beheersing
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0 cursor-pointer hover:bg-slate-50 rounded-xl p-2 -m-2 transition-colors" onClick={() => onNavigate('subjects')} data-id="element-10268">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10269">
                  Zwakste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-10270">
                  <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center" data-id="element-10271">
                    <WeakestIcon className="w-4 h-4" data-id="element-10272" />
                  </div>
                  <div data-id="element-10273">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-10274">
                      {stats.weakestClassModule.title}
                    </div>
                    <div className="text-xs font-medium text-rose-600" data-id="element-10275">
                      {stats.lowestAvg < 101 ? Math.round(stats.lowestAvg) : 0}%
                      beheersing
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Class-wide Weak Spots */}
        {stats.classWideWeakSpots.length > 0 && <motion.section variants={itemVariants} data-id="element-10276">
            <div className="flex items-center justify-between mb-4" data-id="element-10277">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-10278">
                <Target className="w-4 h-4 text-indigo-500" data-id="element-10279" />
                Klas-brede Zwakke Plekken
              </h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-10280">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10281">
                <div className="w-8" data-id="element-10282"></div>
                <div data-id="element-10283">Onderdeel</div>
                <div className="text-right w-32" data-id="element-10284">Moeite mee</div>
                <div className="w-6" data-id="element-10285"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-10286">
                {stats.classWideWeakSpots.map((spot, i) => {
              const moduleDef = MODULES.find(m => m.id === spot.moduleId);
              const IconComp = getModuleIcon(moduleDef?.icon || 'calculator');
              return <motion.div key={spot.familyId} variants={listItemVariants} onClick={() => onNavigate('subjects')} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-10287">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${moduleDef?.bgColor || 'bg-slate-100'} ${moduleDef?.color || 'text-slate-500'}`} data-id="element-10288">
                        <IconComp className="w-4 h-4" data-id="element-10289" />
                      </div>
                      <div className="min-w-0 flex flex-col gap-1" data-id="element-10290">
                        <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-10291">
                          {spot.label}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-10292">
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10293">
                            <div className={`h-full rounded-full ${spot.avgMastery >= 75 ? 'bg-emerald-500' : spot.avgMastery >= 40 ? 'bg-indigo-400' : 'bg-rose-500'}`} style={{
                        width: `${Math.max(2, spot.avgMastery)}%`
                      }} data-id="element-10294" />
                          </div>
                          <span className="text-[10px] font-bold text-slate-400" data-id="element-10295">
                            {spot.avgMastery}% gem.
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end w-32" data-id="element-10296">
                        <span className="font-bold text-xs text-rose-600 bg-rose-50 px-2 py-1 rounded-md" data-id="element-10297">
                          {spot.strugglingCount} van {spot.practicedCount} lln
                        </span>
                      </div>
                      <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-10298">
                        <ArrowRight className="w-4 h-4" data-id="element-10299" />
                      </div>
                    </motion.div>;
            })}
              </motion.div>
            </Card>
          </motion.section>}

        {/* Aandachtspunten is now covered by PriorityAlertBar above.
            Only show a compact "see all" affordance when overflow exists. */}
        {stats.totalStruggling > 3 && <motion.div variants={itemVariants} className="text-center -mt-2" data-id="element-10300">
            <button onClick={() => onNavigate('class')} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors inline-flex items-center gap-1.5 group" data-id="element-10301">
              Bekijk alle {stats.totalStruggling} leerlingen die aandacht nodig
              hebben
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-10302" />
            </button>
          </motion.div>}

        {/* Inactive Students */}
        <motion.section variants={itemVariants} data-id="element-10303">
          <div className="flex items-center justify-between mb-4" data-id="element-10304">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-10305">
              <Clock className="w-4 h-4 text-amber-500" data-id="element-10306" />
              Niet geoefend deze week
            </h2>
          </div>
          {stats.inactiveStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-10307">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10308">
                <div className="w-8" data-id="element-10309"></div>
                <div data-id="element-10310">Naam</div>
                <div className="text-right w-32" data-id="element-10311">Inactief sinds</div>
                <div className="w-6" data-id="element-10312"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-10313">
                {stats.inactiveStudents.slice(0, 5).map(({
              student,
              lastActive
            }, i) => {
              const inactiveLabel = lastActive > 0 ? formatRelativeTime(lastActive) : 'Nog nooit geoefend';
              return <motion.div key={student.id} variants={listItemVariants} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-10314">
                        <div className="relative" data-id="element-10315">
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm shadow-sm`} data-id="element-10316">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500 shadow-sm" role="img" aria-label="Inactief deze week" data-id="element-10317" />
                        </div>
                        <div className="min-w-0" data-id="element-10318">
                          <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-10319">
                            {student.name}
                          </div>
                        </div>
                        <div className="flex items-center justify-end w-32" data-id="element-10320">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${lastActive === 0 ? 'bg-rose-50 text-rose-600' : Date.now() - lastActive > LONG_INACTIVE_MS ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-10321">
                            {inactiveLabel}
                          </span>
                        </div>
                        <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-10322">
                          <ArrowRight className="w-4 h-4" data-id="element-10323" />
                        </div>
                      </motion.div>;
            })}
              </motion.div>
              {stats.inactiveStudents.length > 5 && <div className="p-3 bg-slate-50 border-t border-slate-100 text-center" data-id="element-10324">
                  <button onClick={() => onNavigate('class')} className="text-xs font-bold text-slate-500 hover:text-indigo-600 uppercase tracking-wider transition-colors inline-flex items-center gap-1 group" data-id="element-10325">
                    + {stats.inactiveStudents.length - 5} meer bekijken
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" data-id="element-10326" />
                  </button>
                </div>}
            </Card> : <Card className="p-6 text-center border-emerald-100 bg-emerald-50/50 shadow-sm overflow-hidden flex flex-col items-center justify-center" data-id="element-10327">
              <DjayNarrator size="sm" emotion="happy" data-id="element-10328" />
              <p className="text-emerald-800 font-bold mt-3" data-id="element-10329">
                Iedereen heeft deze week geoefend!
              </p>
            </Card>}
        </motion.section>
      </motion.div>
    </PageLayout>;
}